import { AxiosError, AxiosResponse } from 'axios';
import AxiosInstance from '../../helpers/axios-instance/axios-instance';
import { appConfig } from '../../helpers/app-config/app-config';
import type { InitiativeTasksConfig as InitiativeTasksFilters } from '../initiative-client/initiative-client.type';
import type { MinifiedTask } from '../tasks-client/task-client.type';
import type {
  Goal,
  GoalTasksFilters,
  MindMapData,
  MinimalGoal,
  MinimalOutcome,
  Outcome,
  PreSaveGoal,
  PreSaveOutcome,
  RoadmapData,
  ToggleVisibilityResponse,
} from './strategy-client.type';

const axiosInstance = AxiosInstance();
const baseURL = appConfig.VITE_APP_API_URL;

/**
 * Fetches array of outcomes
 *
 * @param {string} organizationId - organization ID
 * @param {string} portfolioId - portfolio ID
 * @param {boolean} includeHidden - When true, requests outcomes hidden in the given portfolio in addition to visible ones; each item carries `is_hidden` for caller-side rendering decisions
 * @returns {Promise<Outcome[]>} Array of outcomes
 */
async function getOutcomes(organizationId: string, portfolioId?: string, includeHidden?: boolean): Promise<Outcome[]> {
  let url = baseURL + `/api/strategy/outcomes/?organization_id=${organizationId}`;
  if (portfolioId) {
    url += `&portfolio_id=${portfolioId}`;
  }
  if (includeHidden) {
    url += '&include_hidden=true';
  }
  try {
    const response: AxiosResponse = await axiosInstance.get(url);
    return response.data;
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      return Promise.reject(error.response);
    } else {
      return Promise.reject(new Error('An unknown error occurred while fetching outcomes.'));
    }
  }
}

/**
 * Fetches minimal array of outcomes (id and name only)
 *
 * @param {string} organizationId - organization ID
 * @param {string} portfolioId - portfolio ID
 * @returns {Promise<MinimalOutcome[]>} Array of minimal outcomes
 */
async function getOutcomesMinimal(organizationId: string, portfolioId?: string): Promise<MinimalOutcome[]> {
  let url = baseURL + `/api/strategy/outcomes/minimal/?organization_id=${organizationId}`;
  if (portfolioId) {
    url += `&portfolio_id=${portfolioId}`;
  }
  try {
    const response: AxiosResponse = await axiosInstance.get(url);
    return response.data;
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      return Promise.reject(error.response);
    } else {
      return Promise.reject(new Error('An unknown error occurred while fetching minimal outcomes.'));
    }
  }
}

/**
 * Fetches outcome by ID
 *
 * @param outcomeId - outcome ID
 * @returns {Promise<Outcome>} Outcome object
 */
async function getOutcome(outcomeId: string): Promise<Outcome> {
  try {
    const url = baseURL + `/api/strategy/outcomes/${outcomeId}/`;
    const response: AxiosResponse = await axiosInstance.get(url);
    return response.data;
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      return Promise.reject(error.response);
    } else {
      return Promise.reject(new Error(`An unknown error occurred while fetching outcome ${outcomeId}`));
    }
  }
}

/**
 * Creates a new outcome
 *
 * @param {PreSaveOutcome} outcome - outcome data to create
 * @returns {Promise<Outcome>} Created outcome object
 */
async function createOutcome(outcome: PreSaveOutcome): Promise<Outcome> {
  try {
    const url = baseURL + '/api/strategy/outcomes/';
    const response: AxiosResponse = await axiosInstance.post(url, outcome);
    return response.data;
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      return Promise.reject(error.response);
    } else {
      return Promise.reject(new Error('An unknown error occurred while creating outcome.'));
    }
  }
}

/**
 * Updates an existing outcome
 *
 * @param {string} outcomeId - outcome ID to update
 * @param {PreSaveOutcome} outcome - updated outcome data
 * @returns {Promise<Outcome>} Updated outcome object
 */
async function updateOutcome(outcomeId: string, outcome: PreSaveOutcome): Promise<Outcome> {
  try {
    const url = baseURL + `/api/strategy/outcomes/${outcomeId}/`;
    const response: AxiosResponse = await axiosInstance.put(url, outcome);
    return response.data;
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      return Promise.reject(error.response);
    } else {
      return Promise.reject(new Error(`An unknown error occurred while updating outcome ${outcomeId}`));
    }
  }
}

/**
 * Deletes an outcome
 *
 * @param {string} outcomeId - outcome ID to delete
 * @returns {Promise<void>} Promise that resolves when the outcome is deleted
 */
async function deleteOutcome(outcomeId: string): Promise<void> {
  try {
    const url = baseURL + `/api/strategy/outcomes/${outcomeId}/`;
    const response: AxiosResponse = await axiosInstance.delete(url);
    return response.data;
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      return Promise.reject(error.response);
    } else {
      return Promise.reject(new Error('An unknown error occurred while deleting outcome.'));
    }
  }
}

/**
 * Fetches tasks for an outcome
 *
 * @param {string} outcomeId - outcome ID
 * @param {InitiativeTasksFilters} filters - filters to apply
 * @returns {Promise<MinifiedTask[]>} Tasks data object
 */
async function getOutcomeTasks(outcomeId: string, filters: InitiativeTasksFilters): Promise<MinifiedTask[]> {
  try {
    let url = baseURL + `/api/strategy/outcomes/${outcomeId}/tasks/?filter=${filters.filter}&date=${filters.date}`;
    if (filters.epicId) {
      url += `&epic_id=${filters.epicId}`;
    }
    if (filters.projectId) {
      url += `&project_id=${filters.projectId}`;
    }
    if (filters.initiativeId) {
      url += `&initiative_id=${filters.initiativeId}`;
    }
    const response: AxiosResponse = await axiosInstance.get(url);
    return response.data;
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      return Promise.reject(error.response);
    } else {
      return Promise.reject(new Error(`An unknown error occurred while fetching outcome tasks`));
    }
  }
}

/**
 * Fetches array of goals
 *
 * @param {string} organizationId - organization ID
 * @param {string} portfolioId - portfolio ID
 * @returns {Promise<Goal[]>} Array of goals
 */
async function getGoals(organizationId: string, portfolioId?: string): Promise<Goal[]> {
  try {
    let url = baseURL + `/api/strategy/goals/?organization_id=${organizationId}`;
    if (portfolioId) {
      url += `&portfolio_id=${portfolioId}`;
    }
    const response: AxiosResponse = await axiosInstance.get(url);
    return response.data;
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      return Promise.reject(error.response);
    } else {
      return Promise.reject(new Error('An unknown error occurred while fetching goals.'));
    }
  }
}

/**
 * Fetches minimal array of goals (id and name only)
 *
 * @param {string} organizationId - organization ID
 * @param {string} portfolioId - portfolio ID
 * @returns {Promise<MinimalGoal[]>} Array of minimal goals
 */
async function getGoalsMinimal(organizationId: string, portfolioId?: string): Promise<MinimalGoal[]> {
  try {
    let url = baseURL + `/api/strategy/goals/minimal/?organization_id=${organizationId}`;
    if (portfolioId) {
      url += `&portfolio_id=${portfolioId}`;
    }
    const response: AxiosResponse = await axiosInstance.get(url);
    return response.data;
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      return Promise.reject(error.response);
    } else {
      return Promise.reject(new Error('An unknown error occurred while fetching minimal goals.'));
    }
  }
}

/**
 * Fetches goal by ID
 *
 * @param {string} goalId - goal ID
 * @returns {Promise<Goal>} Goal object
 */
async function getGoal(goalId: string): Promise<Goal> {
  try {
    const url = baseURL + `/api/strategy/goals/${goalId}/`;
    const response: AxiosResponse = await axiosInstance.get(url);
    return response.data;
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      return Promise.reject(error.response);
    } else {
      return Promise.reject(new Error(`An unknown error occurred while fetching goal ${goalId}`));
    }
  }
}

/**
 * Creates a new goal
 *
 * @param {PreSaveGoal} goal - goal data to create
 * @returns {Promise<Goal>} Created goal object
 */
async function createGoal(goal: PreSaveGoal): Promise<Goal> {
  try {
    const url = baseURL + '/api/strategy/goals/';
    const response: AxiosResponse = await axiosInstance.post(url, goal);
    return response.data;
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      return Promise.reject(error.response);
    } else {
      return Promise.reject(new Error('An unknown error occurred while creating goal.'));
    }
  }
}

/**
 * Updates an existing goal
 *
 * @param {string} goalId - goal ID to update
 * @param {PreSaveGoal} goal - updated goal data
 * @returns {Promise<Goal>} Updated goal object
 */
async function updateGoal(goalId: string, goal: PreSaveGoal): Promise<Goal> {
  try {
    const url = baseURL + `/api/strategy/goals/${goalId}/`;
    const response: AxiosResponse = await axiosInstance.put(url, goal);
    return response.data;
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      return Promise.reject(error.response);
    } else {
      return Promise.reject(new Error(`An unknown error occurred while updating goal ${goalId}`));
    }
  }
}

/**
 * Deletes a goal
 *
 * @param {string} goalId - goal ID to delete
 * @returns {Promise<void>} Promise that resolves when the goal is deleted
 */
async function deleteGoal(goalId: string): Promise<void> {
  try {
    const url = baseURL + `/api/strategy/goals/${goalId}/`;
    const response: AxiosResponse = await axiosInstance.delete(url);
    return response.data;
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      return Promise.reject(error.response);
    } else {
      return Promise.reject(new Error('An unknown error occurred while deleting goal.'));
    }
  }
}

/**
 * Fetches tasks for a goal
 *
 * @param {string} goalId - goal ID
 * @param {GoalTasksFilters} filters - filters to apply
 * @returns {Promise<MinifiedTask[]>} Tasks data object
 */
async function getGoalTasks(goalId: string, filters: GoalTasksFilters): Promise<MinifiedTask[]> {
  try {
    let url = baseURL + `/api/strategy/goals/${goalId}/tasks/?filter=${filters.filter}&date=${filters.date}`;
    if (filters.epicId) {
      url += `&epic_id=${filters.epicId}`;
    }
    if (filters.projectId) {
      url += `&project_id=${filters.projectId}`;
    }
    if (filters.outcomeId) {
      url += `&outcome_id=${filters.outcomeId}`;
    }
    if (filters.initiativeId) {
      url += `&initiative_id=${filters.initiativeId}`;
    }
    const response: AxiosResponse = await axiosInstance.get(url);
    return response.data;
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      return Promise.reject(error.response);
    } else {
      return Promise.reject(new Error(`An unknown error occurred while fetching goal tasks`));
    }
  }
}

/**
 * Fetches roadmap data for a portfolio
 *
 * @param {string} portfolioId - portfolio ID
 * @param {string} organizationId - organization ID
 * @returns {Promise<RoadmapData>} Roadmap data with goals, outcomes, and initiatives
 */
async function getRoadmap(portfolioId: string, organizationId: string): Promise<RoadmapData> {
  try {
    const url = baseURL + '/api/strategy/roadmap/';
    const response: AxiosResponse = await axiosInstance.get(url, {
      params: {
        portfolio_id: portfolioId,
        organization_id: organizationId,
      },
    });
    return response.data;
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      return Promise.reject(error.response);
    } else {
      return Promise.reject(new Error('An unknown error occurred while fetching roadmap data'));
    }
  }
}

/**
 * Fetches mind map data for a portfolio
 *
 * @param {string} portfolioId - portfolio ID
 * @param {string} organizationId - organization ID (required)
 * @returns {Promise<MindMapData>} Mind map data with nodes and edges
 */
async function getMindMap(portfolioId: string, organizationId: string): Promise<MindMapData> {
  try {
    const url = baseURL + '/api/strategy/mind-map/';
    const response: AxiosResponse = await axiosInstance.get(url, {
      params: {
        portfolio_id: portfolioId,
        organization_id: organizationId,
      },
    });
    return response.data;
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      return Promise.reject(error.response);
    } else {
      return Promise.reject(new Error('An unknown error occurred while fetching mind map data'));
    }
  }
}

/**
 * Fetches aggregated progress chart data for an outcome
 *
 * @param {string} outcomeId - outcome ID
 * @param {number} topN - number of top initiatives to show (default: 6)
 * @returns {Promise<any>} Progress chart data with top N initiatives + Others
 */
async function getOutcomeProgressChartData(outcomeId: string, topN = 6): Promise<any> {
  try {
    const url = baseURL + `/api/strategy/outcomes/${outcomeId}/progress-chart-data/`;
    const response: AxiosResponse = await axiosInstance.get(url, { params: { top_n: topN } });
    return response.data;
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      return Promise.reject(error.response);
    } else {
      return Promise.reject(new Error(`An unknown error occurred while fetching outcome progress chart data`));
    }
  }
}

/**
 * Fetches aggregated team focus chart data for an outcome
 *
 * @param {string} outcomeId - outcome ID
 * @param {number} topN - number of top initiatives to show (default: 6)
 * @returns {Promise<any>} Team focus chart data with top N initiatives + Others
 */
async function getOutcomeTeamFocusChartData(outcomeId: string, topN = 6): Promise<any> {
  try {
    const url = baseURL + `/api/strategy/outcomes/${outcomeId}/team-focus-chart-data/`;
    const response: AxiosResponse = await axiosInstance.get(url, { params: { top_n: topN } });
    return response.data;
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      return Promise.reject(error.response);
    } else {
      return Promise.reject(new Error(`An unknown error occurred while fetching outcome team focus chart data`));
    }
  }
}

/**
 * Fetches aggregated work chart data for an outcome
 *
 * @param {string} outcomeId - outcome ID
 * @param {number} topN - number of top initiatives to show (default: 6)
 * @returns {Promise<any>} Work committed/completed chart data with top N initiatives + Others
 */
async function getOutcomeWorkChartData(outcomeId: string, topN = 6): Promise<any> {
  try {
    const url = baseURL + `/api/strategy/outcomes/${outcomeId}/work-chart-data/`;
    const response: AxiosResponse = await axiosInstance.get(url, { params: { top_n: topN } });
    return response.data;
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      return Promise.reject(error.response);
    } else {
      return Promise.reject(new Error(`An unknown error occurred while fetching outcome work chart data`));
    }
  }
}

/**
 * Fetches aggregated progress chart data for a goal
 *
 * @param {string} goalId - goal ID
 * @param {number} topN - number of top initiatives to show (default: 6)
 * @returns {Promise<any>} Progress chart data with top N initiatives + Others
 */
async function getGoalProgressChartData(goalId: string, topN = 6): Promise<any> {
  try {
    const url = baseURL + `/api/strategy/goals/${goalId}/progress-chart-data/`;
    const response: AxiosResponse = await axiosInstance.get(url, { params: { top_n: topN } });
    return response.data;
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      return Promise.reject(error.response);
    } else {
      return Promise.reject(new Error(`An unknown error occurred while fetching goal progress chart data`));
    }
  }
}

/**
 * Fetches aggregated team focus chart data for a goal
 *
 * @param {string} goalId - goal ID
 * @param {number} topN - number of top initiatives to show (default: 6)
 * @returns {Promise<any>} Team focus chart data with top N initiatives + Others
 */
async function getGoalTeamFocusChartData(goalId: string, topN = 6): Promise<any> {
  try {
    const url = baseURL + `/api/strategy/goals/${goalId}/team-focus-chart-data/`;
    const response: AxiosResponse = await axiosInstance.get(url, { params: { top_n: topN } });
    return response.data;
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      return Promise.reject(error.response);
    } else {
      return Promise.reject(new Error(`An unknown error occurred while fetching goal team focus chart data`));
    }
  }
}

/**
 * Fetches aggregated work chart data for a goal
 *
 * @param {string} goalId - goal ID
 * @param {number} topN - number of top initiatives to show (default: 6)
 * @returns {Promise<any>} Work committed/completed chart data with top N initiatives + Others
 */
async function getGoalWorkChartData(goalId: string, topN = 6): Promise<any> {
  try {
    const url = baseURL + `/api/strategy/goals/${goalId}/work-chart-data/`;
    const response: AxiosResponse = await axiosInstance.get(url, { params: { top_n: topN } });
    return response.data;
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      return Promise.reject(error.response);
    } else {
      return Promise.reject(new Error(`An unknown error occurred while fetching goal work chart data`));
    }
  }
}

/**
 * Bookmarks a goal for the current user
 *
 * @param {string} goalId - goal ID to bookmark
 * @returns {Promise<Goal>} Updated goal object
 */
async function bookmarkGoal(goalId: string): Promise<Goal> {
  try {
    const url = baseURL + `/api/strategy/goals/${goalId}/bookmark/`;
    const response: AxiosResponse = await axiosInstance.post(url);
    return response.data;
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      return Promise.reject(error.response);
    } else {
      return Promise.reject(new Error(`An unknown error occurred while bookmarking goal ${goalId}`));
    }
  }
}

/**
 * Removes bookmark from a goal for the current user
 *
 * @param {string} goalId - goal ID to unbookmark
 * @returns {Promise<Goal>} Updated goal object
 */
async function unbookmarkGoal(goalId: string): Promise<Goal> {
  try {
    const url = baseURL + `/api/strategy/goals/${goalId}/unbookmark/`;
    const response: AxiosResponse = await axiosInstance.post(url);
    return response.data;
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      return Promise.reject(error.response);
    } else {
      return Promise.reject(new Error(`An unknown error occurred while unbookmarking goal ${goalId}`));
    }
  }
}

/**
 * Bookmarks an outcome for the current user
 *
 * @param {string} outcomeId - outcome ID to bookmark
 * @returns {Promise<Outcome>} Updated outcome object
 */
async function bookmarkOutcome(outcomeId: string): Promise<Outcome> {
  try {
    const url = baseURL + `/api/strategy/outcomes/${outcomeId}/bookmark/`;
    const response: AxiosResponse = await axiosInstance.post(url);
    return response.data;
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      return Promise.reject(error.response);
    } else {
      return Promise.reject(new Error(`An unknown error occurred while bookmarking outcome ${outcomeId}`));
    }
  }
}

/**
 * Removes bookmark from an outcome for the current user
 *
 * @param {string} outcomeId - outcome ID to unbookmark
 * @returns {Promise<Outcome>} Updated outcome object
 */
async function unbookmarkOutcome(outcomeId: string): Promise<Outcome> {
  try {
    const url = baseURL + `/api/strategy/outcomes/${outcomeId}/unbookmark/`;
    const response: AxiosResponse = await axiosInstance.post(url);
    return response.data;
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      return Promise.reject(error.response);
    } else {
      return Promise.reject(new Error(`An unknown error occurred while unbookmarking outcome ${outcomeId}`));
    }
  }
}

/**
 * Hides an outcome in a specific portfolio by setting its visibility to false
 *
 * @param {string} outcomeId - outcome ID to hide
 * @param {string} portfolioId - portfolio ID where visibility should be toggled
 * @returns {Promise<ToggleVisibilityResponse>} Response with visibility status
 */
async function hideOutcome(outcomeId: string, portfolioId: string): Promise<ToggleVisibilityResponse> {
  try {
    const url = baseURL + `/api/strategy/outcomes/${outcomeId}/toggle-visibility/`;
    const response: AxiosResponse<ToggleVisibilityResponse> = await axiosInstance.post(url, {
      show: false,
      portfolio_id: portfolioId,
    });
    return response.data;
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      return Promise.reject(error.response);
    } else {
      return Promise.reject(new Error(`An unknown error occurred while hiding outcome ${outcomeId}`));
    }
  }
}

/**
 * Unhides an outcome in a specific portfolio by setting its visibility to true
 *
 * @param {string} outcomeId - outcome ID to unhide
 * @param {string} portfolioId - portfolio ID where visibility should be toggled
 * @returns {Promise<ToggleVisibilityResponse>} Response with visibility status
 */
async function unhideOutcome(outcomeId: string, portfolioId: string): Promise<ToggleVisibilityResponse> {
  try {
    const url = baseURL + `/api/strategy/outcomes/${outcomeId}/toggle-visibility/`;
    const response: AxiosResponse<ToggleVisibilityResponse> = await axiosInstance.post(url, {
      show: true,
      portfolio_id: portfolioId,
    });
    return response.data;
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      return Promise.reject(error.response);
    } else {
      return Promise.reject(new Error(`An unknown error occurred while unhiding outcome ${outcomeId}`));
    }
  }
}

export {
  bookmarkGoal,
  bookmarkOutcome,
  createGoal,
  createOutcome,
  deleteGoal,
  deleteOutcome,
  getGoal,
  getGoalProgressChartData,
  getGoals,
  getGoalsMinimal,
  getGoalTasks,
  getGoalTeamFocusChartData,
  getGoalWorkChartData,
  getMindMap,
  getOutcome,
  getOutcomeProgressChartData,
  getOutcomes,
  getOutcomesMinimal,
  getOutcomeTasks,
  getOutcomeTeamFocusChartData,
  getOutcomeWorkChartData,
  getRoadmap,
  hideOutcome,
  unbookmarkGoal,
  unbookmarkOutcome,
  unhideOutcome,
  updateGoal,
  updateOutcome,
};
